// ============================================
// TASK-9694 S6a — where a screen reads its customer LIST from
// ============================================
// Side-effect module (UMD): attaches `window.CustomerSourceCore`. app.js imports
// it once, before any view module; views call `window.CustomerSourceCore.create`
// with THEIR OWN firestore imports at call time — never a cross-`?v=` ES import
// (Pitfall #1). Node unit tests require() it directly.
//
// WHY. `tenants/{t}/customers` carries every customer's phones, emails,
// contacts[], notes, billing contact and addresses. It used to be readable by
// every driver-PIN session and driver link, and the job board / clock / JSA /
// driver forms subscribed to the whole collection, so all of it sat in every
// driver phone's offline cache. Crew screens now read `customerDirectory`
// (server-written by onCustomerWriteDirectory: name, company, type, status,
// active, internalTimeOnly, requiredFields — functions/src/shared/
// customerDirectory.js) and the rules refuse crew sessions on `customers`.
//
// FALLBACK (deploy order, not a second policy). Hosting ships BEFORE the rules
// that open customerDirectory. In that window a directory read is refused, so a
// `permission-denied` on the preferred collection swaps to the other one — the
// pre-S6a behaviour, nothing new is exposed. After the rules land, a listener
// that had fallen back to `customers` gets refused there and swaps back once.
// Only `permission-denied` swaps; any other error goes to onError. At most two
// swaps per subscription, so a session refused on BOTH never loops.
//
// CREW_ONLY_MODULES must equal the list in firestore.rules custReader —
// tests/task9694-s6a-customer-directory.unit.cjs fails if they drift.
(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.CustomerSourceCore = api;
}(typeof window !== 'undefined' ? window : (typeof globalThis !== 'undefined' ? globalThis : null), function () {
  'use strict';

  const DIRECTORY = 'customerDirectory';
  const CUSTOMERS = 'customers';
  const CREW_ONLY_MODULES = Object.freeze(['clock', 'jobboard', 'maintenance', 'notes']);

  // Pure: true when this session should read the directory FIRST (it cannot read
  // `customers` once the S6a rules are live). Driver PIN, driver link, public
  // access, and a staff login whose modules are all crew modules. Owners and
  // legacy members with no modules field read customers. The fallback above
  // means a wrong answer here costs one refused read, never a blank screen.
  function sessionReadsDirectory(as) {
    if (!as) return true;
    if (as.driverModePin === true || as.driverMode || as.isPublicAccess || as.accessToken) return true;
    const m = as.memberAccess;
    if (!m) return false;
    if (m.role === 'owner') return false;
    if (!Array.isArray(m.modules)) return false;
    return !m.modules.some((x) => !CREW_ONLY_MODULES.includes(x));
  }

  function other(c) { return c === DIRECTORY ? CUSTOMERS : DIRECTORY; }
  function denied(e) { return !!e && (e.code === 'permission-denied' || /permission/i.test(String(e.code || ''))); }

  // deps: { db, collection, doc, getDoc, getDocs, onSnapshot, query, orderBy }
  function create(deps) {
    const d = deps || {};
    const ref = (tid, col, orderByName) => {
      const c = d.collection(d.db, 'tenants', tid, col);
      return orderByName && d.query && d.orderBy ? d.query(c, d.orderBy('name')) : c;
    };
    const docsOf = (snap) => snap.docs.map((x) => ({ id: x.id, ...x.data() }));
    const first = (opts) => ((opts && opts.prefer) === CUSTOMERS ? CUSTOMERS : DIRECTORY);

    // Live list. onDocs(list, collectionName). Returns an unsubscribe function
    // that always stops whichever listener is current.
    function subscribe(tid, onDocs, opts) {
      const o = opts || {};
      let unsub = null;
      let stopped = false;
      let swaps = 0;
      const start = (col) => {
        unsub = d.onSnapshot(ref(tid, col, o.orderByName), (snap) => {
          if (!stopped) onDocs(docsOf(snap), col);
        }, (err) => {
          if (stopped) return;
          if (denied(err) && swaps < 2) {
            swaps++;
            try { if (unsub) unsub(); } catch (_) { /* already detached */ }
            console.warn(`[CustomerSource] ${o.label || 'customers'}: ${col} refused — reading ${other(col)}`);
            start(other(col));
            return;
          }
          if (typeof o.onError === 'function') o.onError(err, col);
          else console.error(`[CustomerSource] ${o.label || 'customers'} subscription error (${col}):`, err);
        });
      };
      start(first(o));
      return () => { stopped = true; try { if (unsub) unsub(); } catch (_) { /* already detached */ } };
    }

    // One-shot list. Throws only when BOTH collections fail.
    async function getAll(tid, opts) {
      const o = opts || {};
      const col = first(o);
      try {
        return docsOf(await d.getDocs(ref(tid, col, o.orderByName)));
      } catch (e) {
        if (!denied(e)) throw e;
        return docsOf(await d.getDocs(ref(tid, other(col), o.orderByName)));
      }
    }

    // One doc's data, or null when it does not exist. Throws only when BOTH fail.
    async function getOne(tid, id, opts) {
      const col = first(opts);
      const read = async (c) => {
        const s = await d.getDoc(d.doc(d.db, 'tenants', tid, c, id));
        return s.exists() ? s.data() : null;
      };
      try { return await read(col); } catch (e) {
        if (!denied(e)) throw e;
        return read(other(col));
      }
    }

    return { subscribe, getAll, getOne };
  }

  return { DIRECTORY, CUSTOMERS, CREW_ONLY_MODULES, sessionReadsDirectory, create };
}));
